import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useNavigate, useParams } from 'react-router'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { SkeletonFields } from '@/components/ui/skeleton'
import { useVinLookup, VinAttribute, VinLookupResponse, VinParsedEquipment } from '../../../../models/vin'
import { LookupField } from './LookupField'

const LAST_VIN_STORAGE_KEY = 'vin.lastVin'

// vPIC variables worth pulling to the top; the rest still render in the full list below.
const SUMMARY_VARIABLES = [
  'Make',
  'Manufacturer Name',
  'Model',
  'Model Year',
  'Vehicle Type',
  'Body Class',
  'Cab Type',
  'Gross Vehicle Weight Rating From',
  'Axles',
  'Drive Type',
  'Engine Model',
  'Fuel Type - Primary',
  'Trailer Body Type',
  'Trailer Type Connection',
]

function normalizeVin(raw: string): string {
  return raw.replace(/[\s-]/g, '').toUpperCase()
}

export function Vin() {
  const navigate = useNavigate()
  const { vin } = useParams<{ vin?: string }>()
  const [input, setInput] = useState(vin ?? '')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (vin) {
      setInput(vin)
      return
    }
    const stored = localStorage.getItem(LAST_VIN_STORAGE_KEY)
    if (stored) setInput(stored)
  }, [vin])

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const normalized = normalizeVin(input)
    if (!/^[A-Z0-9]+$/.test(normalized)) {
      setError('VIN must contain letters and numbers only')
      return
    }
    setError(null)
    localStorage.setItem(LAST_VIN_STORAGE_KEY, normalized)
    navigate(`/insure/internal/vin/${normalized}`)
  }

  return (
    <>
      <Helmet>
        <title>VIN Lookup</title>
      </Helmet>
      <div className="flex flex-col gap-5">
        <div className="text-2xl font-semibold">VIN Lookup</div>
        <form onSubmit={handleSubmit} className="flex flex-col gap-1">
          <label htmlFor="vin" className="text-sm font-medium">
            VIN
          </label>
          <div className="flex items-center gap-3">
            <Input
              id="vin"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="e.g. 1FUJGLDR0LLAA1111"
              className="w-80 font-mono"
              autoFocus
            />
            <Button type="submit">Look up</Button>
          </div>
          {error && <span className="text-destructive text-xs">{error}</span>}
        </form>

        {vin && <VinResults vin={vin} />}
      </div>
    </>
  )
}

function VinResults({ vin }: { vin: string }) {
  const lookup = useVinLookup(vin)

  if (lookup.isError) {
    return (
      <Card>
        <CardContent className="text-destructive text-sm">
          NHTSA VIN decode failed. Try again in a moment.
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <VehicleCard isLoading={lookup.isLoading} data={lookup.data ?? null} />
      <CaiMappingCard isLoading={lookup.isLoading} parsed={lookup.data?.parsed ?? null} />
      <AttributesCard isLoading={lookup.isLoading} attributes={lookup.data?.attributes ?? []} />
    </div>
  )
}

function DecodeStatus({ data }: { data: VinLookupResponse }) {
  const clean = data.errorCode === '0'
  return (
    <div className="flex items-center gap-2">
      <Badge variant={clean ? 'success' : 'destructive'}>{clean ? 'Decoded clean' : 'Partial decode'}</Badge>
      {data.errorText && <span className="text-muted-foreground text-xs">{data.errorText}</span>}
    </div>
  )
}

function VehicleCard({ isLoading, data }: { isLoading: boolean; data: VinLookupResponse | null }) {
  const byVariable = new Map((data?.attributes ?? []).map((a) => [a.variable, a.value]))

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle>Vehicle</CardTitle>
          {!isLoading && data && <DecodeStatus data={data} />}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <SkeletonFields fields={12} />
        ) : !data || data.attributes.length === 0 ? (
          <div className="text-muted-foreground text-sm">NHTSA returned no data for this VIN.</div>
        ) : (
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <LookupField label="VIN" value={data.vin} />
            {SUMMARY_VARIABLES.map((variable) => (
              <LookupField key={variable} label={variable} value={byVariable.get(variable)} />
            ))}
          </dl>
        )}
      </CardContent>
    </Card>
  )
}

function CaiMappingCard({ isLoading, parsed }: { isLoading: boolean; parsed: VinParsedEquipment | null }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Cai Equipment Mapping</CardTitle>
        <CardDescription>What we would store if this VIN were added to a fleet.</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <SkeletonFields fields={6} />
        ) : !parsed || Object.keys(parsed).length === 0 ? (
          <div className="text-muted-foreground text-sm">Nothing mapped from this VIN.</div>
        ) : (
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <LookupField label="Make" value={parsed.make} />
            <LookupField label="Model" value={parsed.model} />
            <LookupField label="Year" value={parsed.year} />
            <LookupField label="Vehicle class" value={parsed.vehicleClass} />
            <LookupField label="Weight class" value={parsed.weightClass} />
            <LookupField label="Equipment type" value={parsed.isTrailer ? 'Trailer' : 'Truck'} />
          </dl>
        )}
      </CardContent>
    </Card>
  )
}

function AttributesCard({ isLoading, attributes }: { isLoading: boolean; attributes: VinAttribute[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>All NHTSA Attributes ({attributes.length})</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <SkeletonFields fields={16} />
        ) : attributes.length === 0 ? (
          <div className="text-muted-foreground text-sm">No attributes returned.</div>
        ) : (
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            {attributes.map((attribute) => (
              <LookupField key={attribute.variable} label={attribute.variable} value={attribute.value} />
            ))}
          </dl>
        )}
      </CardContent>
    </Card>
  )
}
